import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Badge, Button, Input, Spinner, Alert } from 'reactstrap';
import { useAuth } from '../../contexts/AuthContext';
import { POLLING_INTERVALS, createSmartInterval } from '../../utils/polling';
import { chatApi, getStoredAccessToken, refreshAccessToken } from '../../services/api';

// Team avatar images
import avatar1 from '../../assets/images/users/avatar-2.jpg';
import avatar2 from '../../assets/images/users/avatar-3.jpg';
import avatar3 from '../../assets/images/users/avatar-5.jpg';

// Header background
import headerBg from '../../assets/images/auth-one-bg.jpg';

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

// Notification sound
const NOTIFICATION_SOUND = "/sounds/notification.mp3";

interface Message {
  id: number;
  roomId: string;
  senderId: string;
  senderName: string;
  message: string;
  createdAt: string;
  isMine: boolean;
  senderType?: 'user' | 'ai' | 'agent' | 'system';
  isStreaming?: boolean;
  fileUrl?: string;
  fileName?: string;
  messageType?: 'text' | 'image' | 'file' | 'system';
}

interface Conversation {
  roomId: string;
  subject: string;
  lastMessage: string;
  lastMessageAt: string;
  unreadCount: number;
  status: 'open' | 'closed';
  rating?: number | null;
  agentKey?: string;
  handoverRecommended?: boolean;
  aiEnabled?: boolean;
}

// Agent display names
const AGENT_NAMES: Record<string, string> = {
  support: 'Support Agent',
  sales: 'Sales Agent',
  tech: 'Technical Support',
  billing: 'Billing Agent'
};

type TabType = 'home' | 'messages' | 'chat' | 'help' | 'selectTopic';

// Support topics for new conversations
const SUPPORT_TOPICS = [
  { icon: '📦', label: 'Order Issue', value: 'Order Issue' },
  { icon: '🛒', label: 'Product Question', value: 'Product Question' },
  { icon: '💳', label: 'Billing & Payment', value: 'Billing & Payment' },
  { icon: '🚚', label: 'Shipping & Delivery', value: 'Shipping & Delivery' },
  { icon: '🔄', label: 'Returns & Refunds', value: 'Returns & Refunds' },
  { icon: '❓', label: 'General Inquiry', value: 'General Inquiry' }
];

const getAuthHeaders = () => {
  const authUser = localStorage.getItem("authUser");
  let token = null;
  if (authUser) {
    try {
      const parsed = JSON.parse(authUser);
      token = parsed.token || parsed.access_token;
    } catch (e) {}
  }
  return {
    "Authorization": `Bearer ${token}`,
    "Content-Type": "application/json"
  };
};

// Fetch with automatic 401 retry (refresh token then retry once).
// Late 401 (token replaced since send) retries with the current token without refreshing;
// otherwise the shared refreshAccessToken stores tokens and token_expires_at in one place.
const fetchWithAuth = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const sentToken = getStoredAccessToken();
  const response = await fetch(url, {
    ...options,
    headers: { ...getAuthHeaders(), ...(options.headers || {}) }
  });

  if (response.status === 401) {
    const currentToken = getStoredAccessToken();
    const newToken = currentToken && currentToken !== sentToken ? currentToken : await refreshAccessToken();
    if (newToken) {
      return fetch(url, {
        ...options,
        headers: {
          ...(options.headers || {}),
          'Authorization': `Bearer ${newToken}`,
          'Content-Type': 'application/json'
        }
      });
    }
  }
  return response;
};

// Common emojis for picker
const EMOJI_LIST = [
  '😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊', '😇', '🙂', '😉', '😌',
  '😍', '🥰', '😘', '😗', '😙', '😚', '😋', '😛', '😜', '🤪', '😝', '🤗',
  '🤔', '🤭', '🤫', '🤥', '😶', '😐', '😑', '😬', '🙄', '😯', '😦', '😧',
  '😮', '😲', '🥱', '😴', '🤤', '😪', '😵', '🤐', '🥴', '🤢', '🤮', '🤧',
  '😷', '🤒', '🤕', '🤑', '🤠', '😈', '👿', '👹', '👺', '🤡', '💀', '👻',
  '👍', '👎', '👌', '✌️', '🤞', '🤟', '🤘', '🤙', '👋', '🖐️', '✋', '👏',
  '🙏', '💪', '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '💯', '✨',
  '🔥', '⭐', '🌟', '💫', '🎉', '🎊', '🎁', '🏆', '✅', '❌', '⚠️', '💡'
];

// Rating options
const RATING_OPTIONS = [
  { emoji: '😠', label: 'Terrible', value: 1 },
  { emoji: '😕', label: 'Bad', value: 2 },
  { emoji: '😐', label: 'Okay', value: 3 },
  { emoji: '😊', label: 'Good', value: 4 },
  { emoji: '🤩', label: 'Amazing', value: 5 }
];

const FloatingChat: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false); // true while open/close animation plays
  const [isVisible, setIsVisible] = useState(false); // controls CSS transform state
  const [activeTab, setActiveTab] = useState<TabType>('home');
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentRoomId, setCurrentRoomId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [totalUnread, setTotalUnread] = useState(0);
  const [lastKnownUnread, setLastKnownUnread] = useState(-1); // Track previous unread for sound
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [conversationEnded, setConversationEnded] = useState(false);
  const [selectedRating, setSelectedRating] = useState<number | null>(null);
  const [viewingHistory, setViewingHistory] = useState(false); // View conversation history when closed
  const [navAnim, setNavAnim] = useState<'forward' | 'back' | 'tab'>('tab'); // Navigation animation direction
  // Helper to get admin avatar based on sender name
  const getAdminAvatarForSender = (senderName: string) => {
    const lowerName = (senderName || '').toLowerCase();
    if (lowerName.includes('james')) return avatar2; // James's avatar
    if (lowerName.includes('emma')) return avatar3; // Emma's avatar
    return avatar1; // Default to Sarah's avatar
  };

  // AI state (disabled but kept for UI)
  const [currentAgent, setCurrentAgent] = useState<string>('support');
  const [handoverRecommended, setHandoverRecommended] = useState(false);

  // Lightbox state for image preview
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastMessageIdRef = useRef<number>(0); // Track last message ID for smart polling
  const fileInputRef = useRef<HTMLInputElement>(null);
  const userIdRef = useRef<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Use AuthContext for user info
  const { user, isAuthenticated } = useAuth();

  // Keep userIdRef updated to avoid stale closure issues in WebSocket callbacks
  useEffect(() => {
    userIdRef.current = user?.user_id || null;
  }, [user?.user_id]);
  const isLoggedIn = isAuthenticated;

  // Get user's first name from AuthContext
  const getUserFirstName = () => {
    if (!user) return 'there';

    // Try first_name first (from profile settings)
    if (user.first_name && user.first_name.trim()) {
      return user.first_name;
    }

    // Try full_name and get first word
    if (user.full_name && user.full_name.trim()) {
      return user.full_name.split(' ')[0];
    }

    // Fallback to email prefix
    if (user.email) {
      return user.email.split('@')[0];
    }

    return 'there';
  };
  const userFirstName = getUserFirstName();

  // Initialize audio
  useEffect(() => {
    audioRef.current = new Audio(NOTIFICATION_SOUND);
    audioRef.current.volume = 0.5;
  }, []);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
  }, []);


  // Fetch all conversations
  const fetchConversations = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/apps/chat/rooms`);
      const data = await response.json();

      if (data.success && data.rooms) {
        // Show both support and direct rooms (admin can initiate chat with users)
        const allRooms = data.rooms
          .filter((r: any) => r.roomType === 'support' || r.roomType === 'direct')
          .map((room: any) => ({
            roomId: room.roomId,
            subject: room.name || (room.roomType === 'direct' ? 'Direct Message' : 'Support Chat'),
            lastMessage: room.lastMessage || '',
            lastMessageAt: room.lastMessageAt || '',
            unreadCount: room.unreadCount || 0,
            status: (room.status === 'closed' ? 'closed' : 'open') as 'open' | 'closed',
            rating: room.rating || null,
            agentKey: room.agentKey || room.agent_key,
            handoverRecommended: room.handoverRecommended || room.handover_recommended || false,
            aiEnabled: room.aiEnabled !== false && room.ai_enabled !== false
          }));

        setConversations(allRooms);

        // Calculate total unread
        const unread = allRooms.reduce((sum: number, r: Conversation) => sum + r.unreadCount, 0);

        // Play sound if unread increased AND chat is not open
        // (if chat is open, user is already seeing messages - no need for sound)
        if (unread > lastKnownUnread && lastKnownUnread >= 0 && !isOpen) {
          playNotificationSound();
        }
        setLastKnownUnread(unread);
        setTotalUnread(unread);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  }, [lastKnownUnread, playNotificationSound, isOpen]);

  // Fetch messages for a room
  const fetchMessages = useCallback(async (roomId: string) => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/apps/chat/rooms/${roomId}/messages`);
      const data = await response.json();

      if (data.success && data.messages) {
        const newMessages: Message[] = data.messages.map((msg: any) => ({
          id: msg.id,
          roomId: msg.roomId,
          senderId: msg.senderId,
          senderName: msg.senderName || 'Support',
          message: msg.message,
          createdAt: msg.createdAt,
          isMine: msg.isMine,
          fileUrl: msg.fileUrl || null,
          fileName: msg.fileName || null,
          messageType: msg.messageType || 'text',
          senderType: msg.senderType || 'user'
        }));

        setMessages(newMessages);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  }, []);

  // Create new conversation
  const createNewConversation = async (subject: string) => {
    try {
      setNavAnim('forward');
      setLoading(true);
      const response = await fetchWithAuth(`${API_URL}/api/apps/chat/support/room`, {
        method: 'POST',
        body: JSON.stringify({ subject })
      });
      const data = await response.json();

      if (data.success) {
        setCurrentRoomId(data.roomId);
        setMessages([]);
        lastMessageIdRef.current = 0;
        setActiveTab('chat');
        await fetchConversations();
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
    } finally {
      setLoading(false);
    }
  };

  // Upload file
  const uploadFile = async (file: File): Promise<Message | null> => {
    if (!currentRoomId) return null;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const data = await chatApi.uploadFile(currentRoomId, formData);

      if (data.success && data.message) {
        return {
          id: data.message.id,
          roomId: currentRoomId,
          senderId: data.message.senderId,
          senderName: 'You',
          message: data.message.message,
          createdAt: data.message.createdAt,
          isMine: true,
          senderType: 'user',
          fileUrl: data.message.fileUrl,
          fileName: data.message.fileName,
          messageType: data.message.isImage ? 'image' : 'file'
        };
      }
      return null;
    } catch (error) {
      console.error('Error uploading file:', error);
      return null;
    }
  };

  // Send message
  const sendMessage = async (messageText?: string) => {
    let textToSend = messageText || newMessage;
    const fileToUpload = selectedFile;

    // Clear inputs immediately
    setNewMessage('');
    setSelectedFile(null);

    // Handle file upload separately
    if (fileToUpload && !messageText) {
      if (!currentRoomId || sending) return;
      setSending(true);

      // Add optimistic message
      const optimisticMessage: Message = {
        id: Date.now(),
        roomId: currentRoomId,
        senderId: user?.user_id || 'user',
        senderName: 'You',
        message: `Uploading ${fileToUpload.name}...`,
        createdAt: new Date().toISOString(),
        isMine: true,
        senderType: 'user',
        fileName: fileToUpload.name,
        messageType: fileToUpload.type.startsWith('image/') ? 'image' : 'file'
      };
      setMessages(prev => [...prev, optimisticMessage]);

      const uploadedMessage = await uploadFile(fileToUpload);
      if (uploadedMessage) {
        // Replace optimistic message with real one
        setMessages(prev => prev.map(m =>
          m.id === optimisticMessage.id ? uploadedMessage : m
        ));
      }

      fetchConversations();
      setSending(false);

      // Send text message too if there is one
      if (!textToSend.trim()) return;
    }

    if (!textToSend.trim() || !currentRoomId || sending) return;

    setSending(true);

    // Add user message to UI immediately
    const userMessage: Message = {
      id: Date.now(),
      roomId: currentRoomId,
      senderId: user?.user_id || 'user',
      senderName: 'You',
      message: textToSend,
      createdAt: new Date().toISOString(),
      isMine: true,
      senderType: 'user',
      messageType: 'text'
    };
    setMessages(prev => [...prev, userMessage]);

    // Send via REST API
    try {
      const response = await fetchWithAuth(`${API_URL}/api/apps/chat/rooms/${currentRoomId}/messages`, {
        method: 'POST',
        body: JSON.stringify({
          roomId: currentRoomId,
          message: textToSend,
          messageType: 'text'
        })
      });
      const data = await response.json();

      if (!data.success) {
        console.error('Error sending message:', data.error);
      }

      lastMessageIdRef.current = data.message?.id || lastMessageIdRef.current + 1;

      // Handle auto-response if present (first message in support chat)
      if (data.auto_response) {
        const autoResponseMessage: Message = {
          id: data.auto_response.id,
          roomId: currentRoomId,
          senderId: 'system_auto',
          senderName: 'Coderom Support',
          message: data.auto_response.message,
          createdAt: data.auto_response.created_at,
          isMine: false,
          senderType: 'agent',
          messageType: 'text'
        };
        setMessages(prev => [...prev, autoResponseMessage]);
        lastMessageIdRef.current = Math.max(lastMessageIdRef.current, data.auto_response.id);
      }

      // Refresh conversations list to update last message
      fetchConversations();
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  // Handle emoji selection
  const handleEmojiSelect = (emoji: string) => {
    setNewMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      closePickers();
    }
  };

  // Close pickers when clicking outside
  const closePickers = () => {
    setShowEmojiPicker(false);
    setShowMenu(false);
  };

  // End conversation - close it on the backend
  const endConversation = async () => {
    setShowMenu(false);
    try {
      await fetchWithAuth(`${API_URL}/api/apps/chat/rooms/${currentRoomId}/close`, {
        method: 'POST'
      });
      setConversationEnded(true);
    } catch (error) {
      console.error('Error closing conversation:', error);
    }
  };

  // Submit rating
  const submitRating = async (rating: number) => {
    setSelectedRating(rating);
    try {
      await fetchWithAuth(`${API_URL}/api/apps/chat/rooms/${currentRoomId}/rate?rating=${rating}`, {
        method: 'POST'
      });
    } catch (error) {
      console.error('Error submitting rating:', error);
    }
    // Close chat after a delay
    setTimeout(() => {
      setNavAnim('back');
      setConversationEnded(false);
      setSelectedRating(null);
      setActiveTab('messages');
      fetchConversations();
    }, 2000);
  };

  // Open a conversation
  const openConversation = async (roomId: string) => {
    setNavAnim('forward');
    setCurrentRoomId(roomId);
    setLoading(true);
    setShowMenu(false);
    setHandoverRecommended(false);
    setViewingHistory(false);
    lastMessageIdRef.current = 0;

    // Find conversation to get agent info and check status
    const conv = conversations.find(c => c.roomId === roomId);
    if (conv) {
      setCurrentAgent(conv.agentKey || 'support');
      setHandoverRecommended(conv.handoverRecommended || false);
      // If conversation is closed, show the ended screen
      setConversationEnded(conv.status === 'closed');
      // Set existing rating if already rated (prevents re-rating)
      setSelectedRating(conv.rating || null);
    } else {
      setConversationEnded(false);
      setSelectedRating(null);
    }

    await fetchMessages(roomId);
    setLoading(false);
    setActiveTab('chat');
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Polling for updates when chat is open
  useEffect(() => {
    if (isOpen) {
      fetchConversations();

      // If in chat view, fetch messages immediately
      if (currentRoomId && activeTab === 'chat') {
        fetchMessages(currentRoomId);
      }

      // Poll conversations every 30 seconds (optimized from 10s)
      // Poll messages every 10 seconds when in chat view (optimized from 3s)
      const conversationCleanup = createSmartInterval(
        fetchConversations,
        POLLING_INTERVALS.CHAT_ROOMS_ACTIVE,
        { runImmediately: false, runOnVisible: true }
      );

      let messageCleanup: (() => void) | null = null;
      if (currentRoomId && activeTab === 'chat') {
        messageCleanup = createSmartInterval(
          () => fetchMessages(currentRoomId),
          POLLING_INTERVALS.CHAT_MESSAGES,
          { runImmediately: false, runOnVisible: true }
        );
      }

      return () => {
        conversationCleanup();
        if (messageCleanup) {
          messageCleanup();
        }
      };
    }
  }, [isOpen, currentRoomId, activeTab, fetchConversations, fetchMessages]);

  // Check for unread when chat is closed/minimized (poll every 30 seconds)
  useEffect(() => {
    if (isLoggedIn && !isOpen) {
      fetchConversations();
      const cleanup = createSmartInterval(
        fetchConversations,
        POLLING_INTERVALS.CHAT_ROOMS_BADGE,
        { runImmediately: false, runOnVisible: true }
      );
      return () => cleanup();
    }
  }, [isLoggedIn, isOpen, fetchConversations]);

  // Handle key press
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isLoggedIn) return null;

  // Format time
  const formatTime = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'Yesterday';
    } else if (days < 7) {
      return `${days}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Simple markdown formatter for auto-response messages
  const formatMessage = (text: string) => {
    if (!text) return null;

    // Split by lines and process
    const lines = text.split('\n');

    return lines.map((line, lineIdx) => {
      // Process bold text (**text**)
      const parts = line.split(/(\*\*[^*]+\*\*)/g);
      const formattedParts = parts.map((part, partIdx) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={partIdx}>{part.slice(2, -2)}</strong>;
        }
        return part;
      });

      return (
        <span key={lineIdx}>
          {formattedParts}
          {lineIdx < lines.length - 1 && <br />}
        </span>
      );
    });
  };

  // Open chat with animation
  const openChat = () => {
    setIsOpen(true);
    setIsAnimating(true);
    // Start in collapsed state, then trigger transition on next frame
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setIsVisible(true);
        setTimeout(() => setIsAnimating(false), 300);
      });
    });
  };

  // Close chat with animation
  const closeChat = () => {
    setIsAnimating(true);
    setIsVisible(false);
    setTimeout(() => {
      setIsOpen(false);
      setIsAnimating(false);
    }, 200);
  };

  // Navigation animation helper
  const getNavAnimation = () => {
    switch (navAnim) {
      case 'forward': return 'fcSlideIn 220ms cubic-bezier(0.4, 0, 0.2, 1) both';
      case 'back': return 'fcSlideBack 220ms cubic-bezier(0.4, 0, 0.2, 1) both';
      case 'tab': return 'fcFadeIn 200ms ease-out both';
    }
  };

  // Team avatars
  const teamAvatars = [
    { name: 'Sarah', image: avatar1 },
    { name: 'James', image: avatar2 },
    { name: 'Emma', image: avatar3 }
  ];

  return (
    <>
      {/* Floating Button */}
      {!isOpen && !isAnimating && (
        <div
          onClick={openChat}
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: '#405189',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 9999,
            transition: 'transform 0.2s'
          }}
          className="floating-chat-btn"
        >
          <i className="ri-message-3-line" style={{ fontSize: '24px', color: 'white' }}></i>
          {totalUnread > 0 && (
            <Badge
              color="danger"
              pill
              style={{
                position: 'absolute',
                top: '-5px',
                right: '-5px',
                minWidth: '22px',
                height: '22px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {totalUnread > 9 ? '9+' : totalUnread}
            </Badge>
          )}
        </div>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            width: '380px',
            height: '550px',
            zIndex: 9999,
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            borderRadius: '16px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'white',
            transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
            opacity: isVisible ? 1 : 0,
            transition: isVisible
              ? 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1), opacity 300ms cubic-bezier(0.4, 0, 0.2, 1)'
              : 'transform 200ms ease-in, opacity 200ms ease-in',
            willChange: 'transform, opacity'
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: activeTab === 'chat' ? '12px 16px' : '24px 16px',
              color: activeTab === 'chat' ? '#212529' : 'white',
              position: 'relative',
              overflow: activeTab === 'chat' ? 'visible' : 'hidden',
              backgroundColor: activeTab === 'chat' ? '#fff' : 'transparent',
              borderBottom: activeTab === 'chat' ? '1px solid #e9ebec' : 'none',
              zIndex: activeTab === 'chat' ? 100 : 'auto'
            }}
          >
            {/* Blurred background image - only show when not in chat */}
            {activeTab !== 'chat' && (
              <>
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundImage: `url(${headerBg})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    filter: 'blur(2px)',
                    transform: 'scale(1.1)',
                    zIndex: 0,
                    pointerEvents: 'none'
                  }}
                />
                {/* Dark overlay for better text readability */}
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(135deg, rgba(64, 81, 137, 0.85) 0%, rgba(10, 179, 156, 0.75) 100%)',
                    zIndex: 1,
                    pointerEvents: 'none'
                  }}
                />
              </>
            )}
            {/* Close button - only show when not in chat (chat has its own close) */}
            {activeTab !== 'chat' && (
              <button
                onClick={closeChat}
                style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  background: 'transparent',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '20px',
                  padding: '8px',
                  zIndex: 10
                }}
              >
                <i className="ri-close-line"></i>
              </button>
            )}

            {activeTab === 'chat' ? (
              <div className="d-flex align-items-center justify-content-between w-100" style={{ position: 'relative', zIndex: 2 }}>
                <div className="d-flex align-items-center gap-2">
                  <button
                    onClick={() => { setShowMenu(false); setNavAnim('back'); setActiveTab('messages'); }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#495057',
                      cursor: 'pointer',
                      fontSize: '18px',
                      padding: '4px'
                    }}
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  {/* Team avatars */}
                  <div className="d-flex" style={{ marginLeft: '4px' }}>
                    {teamAvatars.map((avatar, idx) => (
                      <img
                        key={idx}
                        src={avatar.image}
                        alt={avatar.name}
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          border: '2px solid white',
                          marginLeft: idx > 0 ? '-8px' : 0,
                          objectFit: 'cover'
                        }}
                      />
                    ))}
                  </div>
                  <div style={{ marginLeft: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <img src="/icon.png" alt="CodeRom" style={{ height: '20px', width: 'auto' }} />
                    </div>
                    <div style={{ fontSize: '12px', color: '#0ab39c', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                      <i className="ri-time-line" style={{ fontSize: '12px' }}></i>
                      Under 30 minutes
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-1" style={{ position: 'relative', zIndex: 9999 }}>
                  <button
                    onClick={() => setShowMenu(!showMenu)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#495057',
                      cursor: 'pointer',
                      fontSize: '18px',
                      padding: '4px'
                    }}
                  >
                    <i className="ri-more-fill"></i>
                  </button>
                  {/* Dropdown menu - positioned fixed to avoid overflow issues */}
                  {showMenu && (
                    <div
                      style={{
                        position: 'fixed',
                        bottom: '490px',
                        right: '30px',
                        background: 'white',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        zIndex: 99999,
                        minWidth: '180px'
                      }}
                    >
                      <button
                        onClick={endConversation}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          textAlign: 'left',
                          fontSize: '14px',
                          color: '#212529',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          borderRadius: '8px'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.background = '#f3f6f9'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <i className="ri-logout-circle-line" style={{ color: '#f06548' }}></i>
                        End Conversation
                      </button>
                    </div>
                  )}
                  <button
                    onClick={closeChat}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#495057',
                      cursor: 'pointer',
                      fontSize: '18px',
                      padding: '4px'
                    }}
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ position: 'relative', zIndex: 2 }}>
                {/* Team avatars */}
                <div className="d-flex justify-content-center gap-1 mb-3">
                  {teamAvatars.map((avatar, idx) => (
                    <img
                      key={idx}
                      src={avatar.image}
                      alt={avatar.name}
                      style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: '50%',
                        border: '3px solid white',
                        marginLeft: idx > 0 ? '-10px' : 0,
                        objectFit: 'cover',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                    />
                  ))}
                </div>

                {/* Welcome text */}
                <div style={{ textAlign: 'center' }}>
                  <h3 style={{ margin: 0, fontWeight: 700, color: 'white', textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>Hi {userFirstName}! 👋</h3>
                  <p style={{ margin: '6px 0 0', opacity: 0.95, fontSize: '15px', color: 'white' }}>How can we help you?</p>
                </div>
              </div>
            )}
          </div>

          {/* Content */}
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {/* Home Tab */}
            {activeTab === 'home' && (
              <div style={{ flex: 1, padding: '16px', overflowY: 'auto', animation: getNavAnimation() }}>
                {/* Recent conversation */}
                {conversations.length > 0 && (
                  <div
                    onClick={() => openConversation(conversations[0].roomId)}
                    style={{
                      background: 'white',
                      borderRadius: '12px',
                      padding: '16px',
                      marginBottom: '12px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      cursor: 'pointer',
                      border: '1px solid #e9ebec'
                    }}
                  >
                    <div style={{ fontSize: '12px', color: '#878a99', marginBottom: '8px', fontWeight: 600 }}>
                      Recent message
                    </div>
                    <div className="d-flex align-items-center gap-3">
                      <div
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: '#405189',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          flexShrink: 0
                        }}
                      >
                        <i className="ri-customer-service-2-line"></i>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, color: '#212529', marginBottom: '2px' }}>
                          {conversations[0].subject || 'Support Chat'}
                        </div>
                        <div style={{ fontSize: '13px', color: '#495057', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {conversations[0].lastMessage || 'New conversation'}
                        </div>
                        <div style={{ fontSize: '11px', color: '#878a99', marginTop: '2px' }}>
                          {formatTime(conversations[0].lastMessageAt)}
                        </div>
                      </div>
                      <i className="ri-arrow-right-s-line" style={{ color: '#878a99', fontSize: '20px' }}></i>
                    </div>
                  </div>
                )}

                {/* Start new chat */}
                <div
                  onClick={() => { setNavAnim('forward'); setActiveTab('selectTopic'); }}
                  style={{
                    background: 'white',
                    borderRadius: '12px',
                    padding: '16px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    cursor: 'pointer',
                    border: '1px solid #e9ebec'
                  }}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div style={{ fontWeight: 600, color: '#212529' }}>Send us a message</div>
                      <div style={{ fontSize: '12px', color: '#878a99' }}>We typically reply within 30 minutes</div>
                    </div>
                    <div
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        backgroundColor: '#405189',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white'
                      }}
                    >
                      <i className="ri-send-plane-fill"></i>
                    </div>
                  </div>
                </div>

              </div>
            )}

            {/* Messages Tab */}
            {activeTab === 'messages' && (
              <div style={{ flex: 1, overflowY: 'auto', animation: getNavAnimation() }}>
                {conversations.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 20px', color: '#878a99' }}>
                    <i className="ri-message-3-line" style={{ fontSize: '48px', marginBottom: '12px', display: 'block' }}></i>
                    <p>No conversations yet</p>
                    <Button color="primary" size="sm" onClick={() => { setNavAnim('forward'); setActiveTab('selectTopic'); }}>
                      Start New Chat
                    </Button>
                  </div>
                ) : (
                  conversations.map((conv, idx) => (
                    <div
                      key={conv.roomId}
                      onClick={() => openConversation(conv.roomId)}
                      style={{
                        padding: '16px',
                        borderBottom: '1px solid #e9ebec',
                        cursor: 'pointer',
                        backgroundColor: conv.unreadCount > 0 ? '#f3f6f9' : 'white'
                      }}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <div
                          style={{
                            width: '44px',
                            height: '44px',
                            borderRadius: '50%',
                            backgroundColor: '#405189',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            flexShrink: 0
                          }}
                        >
                          <i className="ri-customer-service-2-line"></i>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="d-flex justify-content-between align-items-center">
                            <span style={{ fontWeight: 600, color: conv.status === 'closed' ? '#878a99' : '#212529' }}>
                              {conv.subject || `Support #${idx + 1}`}
                            </span>
                            <span style={{ fontSize: '11px', color: '#878a99' }}>
                              {formatTime(conv.lastMessageAt)}
                            </span>
                          </div>
                          <div
                            style={{
                              fontSize: '13px',
                              color: '#878a99',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {conv.lastMessage || 'New conversation'}
                          </div>
                        </div>
                        {conv.status === 'closed' ? (
                          <Badge color="secondary" pill style={{ fontSize: '10px' }}>
                            Closed
                          </Badge>
                        ) : conv.unreadCount > 0 ? (
                          <Badge color="danger" pill>
                            {conv.unreadCount}
                          </Badge>
                        ) : null}
                      </div>
                    </div>
                  ))
                )}

              </div>
            )}

            {/* Topic Selection Tab */}
            {activeTab === 'selectTopic' && (
              <div style={{ flex: 1, overflowY: 'auto', padding: '16px', animation: getNavAnimation() }}>
                {/* Back button */}
                <button
                  onClick={() => { setNavAnim('back'); setActiveTab('home'); }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: '#405189',
                    fontSize: '14px',
                    padding: '0',
                    marginBottom: '16px'
                  }}
                >
                  <i className="ri-arrow-left-line"></i>
                  Back
                </button>

                {/* Title */}
                <h5 style={{
                  color: '#212529',
                  marginBottom: '8px',
                  fontWeight: 600
                }}>
                  What can we help you with?
                </h5>
                <p style={{
                  color: '#878a99',
                  fontSize: '13px',
                  marginBottom: '20px'
                }}>
                  Select a topic to start the conversation
                </p>

                {/* Topic buttons */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {SUPPORT_TOPICS.map((topic) => (
                    <button
                      key={topic.value}
                      onClick={() => createNewConversation(topic.value)}
                      disabled={loading}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '14px 16px',
                        background: 'white',
                        border: '1px solid #e9ebec',
                        borderRadius: '10px',
                        cursor: loading ? 'wait' : 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.2s ease',
                        opacity: loading ? 0.7 : 1
                      }}
                      onMouseOver={(e) => {
                        if (!loading) {
                          e.currentTarget.style.borderColor = '#405189';
                          e.currentTarget.style.backgroundColor = '#f8f9ff';
                        }
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.borderColor = '#e9ebec';
                        e.currentTarget.style.backgroundColor = 'white';
                      }}
                    >
                      <span style={{ fontSize: '24px' }}>{topic.icon}</span>
                      <span style={{
                        fontWeight: 500,
                        color: '#212529',
                        fontSize: '14px'
                      }}>
                        {topic.label}
                      </span>
                      {loading && (
                        <Spinner size="sm" color="primary" style={{ marginLeft: 'auto' }} />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Chat Tab */}
            {activeTab === 'chat' && (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', animation: getNavAnimation() }}>
                {conversationEnded && !viewingHistory ? (
                  /* End Conversation & Rating Screen */
                  <div
                    style={{
                      flex: 1,
                      overflowY: 'auto',
                      padding: '16px',
                      backgroundColor: '#f3f3f9',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    {(() => {
                      // Get last admin message sender info
                      const lastAdminMsg = [...messages].reverse().find(m => !m.isMine && m.senderType !== 'system');
                      const closingAdminName = lastAdminMsg?.senderName || 'Support';
                      const closingAdminAvatar = getAdminAvatarForSender(closingAdminName);
                      return (
                        <>
                          {/* Closing message */}
                          <div
                            style={{
                              backgroundColor: 'white',
                              borderRadius: '12px',
                              padding: '16px',
                              marginBottom: '16px'
                            }}
                          >
                            <div className="d-flex align-items-start gap-3">
                              <img
                                src={closingAdminAvatar}
                                alt={closingAdminName}
                                style={{
                                  width: '40px',
                                  height: '40px',
                                  borderRadius: '50%',
                                  objectFit: 'cover'
                                }}
                              />
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 600, color: '#212529', marginBottom: '4px' }}>
                                  {closingAdminName}
                                </div>
                                <p style={{ margin: '0 0 12px', color: '#495057', fontSize: '14px', lineHeight: '1.5' }}>
                                  Hello! If you have no further questions, we can go ahead and close the chat. If anything comes up later, feel free to reach out again - we're always here to help.
                                </p>
                                <p style={{ margin: '0 0 12px', color: '#495057', fontSize: '14px' }}>
                                  If you need anything at all we'll be here to help! 👍
                                </p>
                                <p style={{ margin: '0 0 8px', color: '#495057', fontSize: '14px', fontWeight: 500 }}>
                                  Rate our work at the end of the dialogue!
                                </p>
                                <p style={{ margin: 0, color: '#0ab39c', fontWeight: 600, fontSize: '14px' }}>
                                  Thank you!
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Rating section */}
                          <div
                            style={{
                              backgroundColor: 'white',
                              borderRadius: '12px',
                              padding: '20px',
                              textAlign: 'center'
                            }}
                          >
                            <div
                              style={{
                                fontSize: '14px',
                                color: '#878a99',
                                marginBottom: '16px'
                              }}
                            >
                              Help <strong style={{ color: '#212529' }}>{closingAdminName}</strong> understand how they're doing
                            </div>
                            <div style={{ fontSize: '13px', color: '#878a99', marginBottom: '12px' }}>
                              Rate your conversation
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: '8px'
                              }}
                            >
                              {RATING_OPTIONS.map((option) => (
                                <button
                                  key={option.value}
                                  onClick={() => submitRating(option.value)}
                                  disabled={selectedRating !== null}
                                  style={{
                                    background: selectedRating === option.value ? '#405189' : 'transparent',
                                    border: '2px solid',
                                    borderColor: selectedRating === option.value ? '#405189' : '#e9ebec',
                                    borderRadius: '12px',
                                    padding: '12px 8px',
                                    cursor: selectedRating !== null ? 'default' : 'pointer',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '4px',
                                    transition: 'all 0.2s',
                                    opacity: selectedRating !== null && selectedRating !== option.value ? 0.5 : 1
                                  }}
                                  onMouseOver={(e) => {
                                    if (selectedRating === null) {
                                      e.currentTarget.style.borderColor = '#405189';
                                    }
                                  }}
                                  onMouseOut={(e) => {
                                    if (selectedRating === null && selectedRating !== option.value) {
                                      e.currentTarget.style.borderColor = '#e9ebec';
                                    }
                                  }}
                                >
                                  <span style={{ fontSize: '24px' }}>{option.emoji}</span>
                                  <span
                                    style={{
                                      fontSize: '10px',
                                      color: selectedRating === option.value ? 'white' : '#878a99',
                                      fontWeight: 500
                                    }}
                                  >
                                    {option.label}
                                  </span>
                                </button>
                              ))}
                            </div>
                            {selectedRating !== null && (
                              <div style={{ marginTop: '16px', color: '#0ab39c', fontWeight: 500 }}>
                                Thank you for your feedback! 🙏
                              </div>
                            )}
                          </div>

                          {/* View Conversation History button */}
                          <button
                            onClick={() => setViewingHistory(true)}
                            style={{
                              marginTop: '16px',
                              padding: '12px 20px',
                              backgroundColor: 'transparent',
                              border: '1px solid #405189',
                              borderRadius: '8px',
                              color: '#405189',
                              fontSize: '14px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '8px',
                              transition: 'all 0.2s'
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.backgroundColor = '#405189';
                              e.currentTarget.style.color = 'white';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.color = '#405189';
                            }}
                          >
                            <i className="ri-history-line"></i>
                            View Conversation History
                          </button>
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  /* Normal Chat Messages */
                  <>
                    {/* Closed Conversation Banner - shown when viewing history */}
                    {conversationEnded && viewingHistory && (
                      <div
                        style={{
                          padding: '10px 16px',
                          backgroundColor: '#f3f6f9',
                          borderBottom: '1px solid #e9ebec',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '8px'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <i className="ri-lock-line" style={{ color: '#878a99' }}></i>
                          <span style={{ fontSize: '13px', color: '#495057' }}>This conversation is closed</span>
                        </div>
                        <button
                          onClick={() => setViewingHistory(false)}
                          style={{
                            padding: '4px 12px',
                            backgroundColor: '#405189',
                            border: 'none',
                            borderRadius: '4px',
                            color: 'white',
                            fontSize: '12px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <i className="ri-arrow-left-line"></i>
                          Back
                        </button>
                      </div>
                    )}

                    {/* Handover Banner */}
                    {handoverRecommended && (
                      <Alert
                        color="warning"
                        style={{
                          margin: '0',
                          borderRadius: '0',
                          padding: '8px 16px',
                          fontSize: '13px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        <i className="ri-user-voice-line"></i>
                        <span>A human agent will join shortly to assist you.</span>
                      </Alert>
                    )}

                    {/* Agent Indicator */}
                    {currentAgent && (
                      <div
                        style={{
                          padding: '6px 16px',
                          backgroundColor: '#f0f4ff',
                          borderBottom: '1px solid #e0e6f0',
                          fontSize: '12px',
                          color: '#405189',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <i className="ri-robot-line"></i>
                        <span>Chatting with <strong>{AGENT_NAMES[currentAgent] || 'AI Assistant'}</strong></span>
                        <span style={{ marginLeft: 'auto', color: '#0ab39c', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#0ab39c' }}></span>
                          Online
                        </span>
                      </div>
                    )}

                    <div
                      style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '16px',
                        backgroundColor: '#f3f3f9'
                      }}
                    >
                      {loading ? (
                        <div className="text-center py-5">
                          <Spinner color="primary" />
                        </div>
                      ) : messages.length === 0 ? (
                        <div className="text-center py-5">
                          <i className="ri-message-3-line" style={{ fontSize: '48px', color: '#878a99' }}></i>
                          <p className="mt-2 text-muted">Start a conversation with our support team!</p>
                        </div>
                      ) : (
                        <>
                          {messages.map((msg, index) => (
                            msg.messageType === 'system' || msg.senderType === 'system' ? (
                              /* System message - centered, different style */
                              <div
                                key={msg.id || index}
                                className="d-flex justify-content-center mb-3"
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '6px 16px',
                                    backgroundColor: '#e8f5e9',
                                    borderRadius: '16px',
                                    fontSize: '12px',
                                    color: '#2e7d32'
                                  }}
                                >
                                  <img
                                    src={getAdminAvatarForSender(msg.senderName || '')}
                                    alt={msg.senderName || 'Support'}
                                    style={{
                                      width: '20px',
                                      height: '20px',
                                      borderRadius: '50%',
                                      objectFit: 'cover'
                                    }}
                                  />
                                  <span>{formatMessage(msg.message)}</span>
                                </div>
                              </div>
                            ) : (
                            <div
                              key={msg.id || index}
                              className={`d-flex mb-3 ${msg.isMine ? 'justify-content-end' : 'justify-content-start'}`}
                            >
                              {/* Avatar and name for non-user messages (admin or AI) */}
                              {!msg.isMine && (
                                <div style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  marginRight: '8px',
                                  flexShrink: 0
                                }}>
                                  {msg.senderType === 'ai' ? (
                                    <div
                                      style={{
                                        width: '28px',
                                        height: '28px',
                                        borderRadius: '50%',
                                        backgroundColor: '#405189',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                    >
                                      <i className="ri-robot-line" style={{ color: 'white', fontSize: '14px' }}></i>
                                    </div>
                                  ) : (
                                    <img
                                      src={getAdminAvatarForSender(msg.senderName || '')}
                                      alt={msg.senderName || 'Support'}
                                      style={{
                                        width: '28px',
                                        height: '28px',
                                        borderRadius: '50%',
                                        objectFit: 'cover'
                                      }}
                                    />
                                  )}
                                  <span style={{
                                    fontSize: '9px',
                                    color: '#878a99',
                                    marginTop: '2px',
                                    fontWeight: 500
                                  }}>
                                    {msg.senderType === 'ai' ? 'AI' : (msg.senderName || 'Support')}
                                  </span>
                                </div>
                              )}
                              <div
                                style={{
                                  maxWidth: '80%',
                                  padding: '10px 14px',
                                  borderRadius: msg.isMine ? '12px 12px 0 12px' : '12px 12px 12px 0',
                                  backgroundColor: msg.isMine ? '#405189' : 'white',
                                  color: msg.isMine ? 'white' : '#212529',
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                }}
                              >
                                {/* Message content - handle images and files */}
                                {msg.messageType === 'image' && msg.fileUrl ? (
                                  <div>
                                    <img
                                      src={`${API_URL}${msg.fileUrl}`}
                                      alt={msg.fileName || 'Image'}
                                      style={{
                                        maxWidth: '100%',
                                        maxHeight: '200px',
                                        borderRadius: '8px',
                                        cursor: 'zoom-in'
                                      }}
                                      onClick={() => setLightboxImage(`${API_URL}${msg.fileUrl}`)}
                                    />
                                    {msg.fileName && (
                                      <div style={{ fontSize: '11px', marginTop: '4px', opacity: 0.8 }}>{msg.fileName}</div>
                                    )}
                                  </div>
                                ) : msg.messageType === 'file' && msg.fileUrl ? (
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                      padding: '8px',
                                      backgroundColor: msg.isMine ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                                      borderRadius: '6px',
                                      cursor: 'pointer'
                                    }}
                                    onClick={() => window.open(`${API_URL}${msg.fileUrl}`, '_blank')}
                                  >
                                    <i className="ri-file-line" style={{ fontSize: '20px' }}></i>
                                    <div>
                                      <div style={{ fontSize: '13px', fontWeight: 500 }}>{msg.fileName || 'File'}</div>
                                      <div style={{ fontSize: '11px', opacity: 0.7 }}>Click to download</div>
                                    </div>
                                  </div>
                                ) : (
                                  <div style={{ fontSize: '14px', lineHeight: '1.5' }}>{formatMessage(msg.message)}</div>
                                )}
                                <div
                                  style={{
                                    fontSize: '10px',
                                    marginTop: '4px',
                                    opacity: 0.7,
                                    textAlign: 'right'
                                  }}
                                >
                                  {formatTime(msg.createdAt)}
                                </div>
                              </div>
                              {/* User name label for own messages */}
                              {msg.isMine && (
                                <div
                                  style={{
                                    marginLeft: '8px',
                                    display: 'flex',
                                    alignItems: 'flex-end',
                                    flexShrink: 0
                                  }}
                                >
                                  <span style={{
                                    fontSize: '11px',
                                    color: '#878a99',
                                    fontWeight: 500
                                  }}>
                                    {user?.full_name || user?.first_name || user?.email?.split('@')[0] || 'You'}
                                  </span>
                                </div>
                              )}
                            </div>
                            )
                          ))}

                        </>
                      )}
                      <div ref={messagesEndRef} />
                    </div>

                {/* Input */}
                <div
                  style={{
                    padding: '12px 16px',
                    borderTop: '1px solid #e9ebec',
                    backgroundColor: 'white'
                  }}
                >
                  {conversationEnded && viewingHistory ? (
                    /* Disabled input for closed conversations */
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        padding: '12px',
                        backgroundColor: '#f3f6f9',
                        borderRadius: '24px',
                        color: '#878a99',
                        fontSize: '13px'
                      }}
                    >
                      <i className="ri-lock-line"></i>
                      <span>This conversation is closed</span>
                    </div>
                  ) : (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      border: '2px solid #405189',
                      borderRadius: '24px',
                      padding: '8px 12px',
                      backgroundColor: '#fff'
                    }}
                  >
                    <input
                      type="text"
                      name="chatMessage"
                      placeholder="Message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      onFocus={closePickers}
                      disabled={sending}
                      autoComplete="off"
                      data-form-type="other"
                      data-lpignore="true"
                      style={{
                        flex: 1,
                        border: 'none',
                        outline: 'none',
                        fontSize: '14px',
                        backgroundColor: 'transparent'
                      }}
                    />
                    <button
                      onClick={() => { closePickers(); sendMessage(); }}
                      disabled={(!newMessage.trim() && !selectedFile) || sending}
                      style={{
                        background: (newMessage.trim() || selectedFile) ? '#405189' : '#e9ebec',
                        border: 'none',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: (newMessage.trim() || selectedFile) ? 'pointer' : 'default',
                        transition: 'background 0.2s'
                      }}
                    >
                      {sending ? (
                        <Spinner size="sm" color="light" />
                      ) : (
                        <i className="ri-arrow-up-line" style={{ color: (newMessage.trim() || selectedFile) ? 'white' : '#878a99', fontSize: '16px' }}></i>
                      )}
                    </button>
                  </div>
                  )}
                  {/* Hidden file input and action icons - only show when conversation is active */}
                  {!(conversationEnded && viewingHistory) && (
                    <>
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                        accept="image/*,.pdf,.doc,.docx,.txt"
                      />

                      {/* Action icons */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '16px',
                          marginTop: '8px',
                          paddingLeft: '4px',
                          position: 'relative'
                        }}
                      >
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px',
                            color: selectedFile ? '#405189' : '#878a99'
                          }}
                          title="Attach file"
                        >
                          <i className="ri-attachment-2" style={{ fontSize: '18px' }}></i>
                        </button>
                        <button
                          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px',
                            color: showEmojiPicker ? '#405189' : '#878a99'
                          }}
                          title="Emoji"
                        >
                          <i className="ri-emotion-line" style={{ fontSize: '18px' }}></i>
                        </button>

                        {/* Emoji Picker */}
                        {showEmojiPicker && (
                          <div
                            style={{
                              position: 'absolute',
                              bottom: '40px',
                              left: '0',
                              background: 'white',
                              borderRadius: '12px',
                              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                              padding: '12px',
                              width: '280px',
                              maxHeight: '200px',
                              overflowY: 'auto',
                              zIndex: 100
                            }}
                          >
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: '4px' }}>
                              {EMOJI_LIST.map((emoji, idx) => (
                                <button
                                  key={idx}
                                  onClick={() => handleEmojiSelect(emoji)}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '20px',
                                    padding: '4px',
                                    borderRadius: '4px',
                                    transition: 'background 0.2s'
                                  }}
                                  onMouseOver={(e) => e.currentTarget.style.background = '#f3f3f9'}
                                  onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                >
                                  {emoji}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                      </div>
                    </>
                  )}

                  {/* Selected file indicator */}
                  {selectedFile && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginTop: '8px',
                        padding: '8px 12px',
                        background: '#f3f6f9',
                        borderRadius: '8px',
                        fontSize: '12px'
                      }}
                    >
                      <i className="ri-file-line" style={{ color: '#405189' }}></i>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {selectedFile.name}
                      </span>
                      <button
                        onClick={() => setSelectedFile(null)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#878a99',
                          padding: '2px'
                        }}
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    </div>
                  )}
                </div>
                  </>
                )}
              </div>
            )}

            {/* Help Tab */}
            {activeTab === 'help' && (
              <div style={{ flex: 1, padding: '16px', overflowY: 'auto', animation: getNavAnimation() }}>
                <div style={{ marginBottom: '16px' }}>
                  <Input
                    type="text"
                    placeholder="Search help articles..."
                    style={{ borderRadius: '20px' }}
                  />
                </div>

                <div style={{ color: '#878a99', textAlign: 'center', padding: '40px 20px' }}>
                  <i className="ri-question-line" style={{ fontSize: '48px', marginBottom: '12px', display: 'block' }}></i>
                  <p>Help center coming soon</p>
                  <Button color="primary" size="sm" onClick={() => { setNavAnim('tab'); setActiveTab('home'); }}>
                    Contact Support
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Bottom Navigation */}
          {activeTab !== 'chat' && (
            <div
              style={{
                display: 'flex',
                borderTop: '1px solid #e9ebec',
                backgroundColor: 'white'
              }}
            >
              <button
                onClick={() => { setNavAnim('tab'); setActiveTab('home'); }}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  color: activeTab === 'home' ? '#405189' : '#878a99',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <i className="ri-home-4-line" style={{ fontSize: '20px' }}></i>
                <span style={{ fontSize: '11px' }}>Home</span>
              </button>
              <button
                onClick={() => { setNavAnim('tab'); setActiveTab('messages'); }}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  color: activeTab === 'messages' ? '#405189' : '#878a99',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px',
                  position: 'relative'
                }}
              >
                <i className="ri-message-2-line" style={{ fontSize: '20px' }}></i>
                <span style={{ fontSize: '11px' }}>Messages</span>
                {totalUnread > 0 && (
                  <Badge
                    color="danger"
                    pill
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '25%',
                      minWidth: '18px',
                      height: '18px',
                      fontSize: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {totalUnread > 9 ? '9+' : totalUnread}
                  </Badge>
                )}
              </button>
              <button
                onClick={() => { setNavAnim('tab'); setActiveTab('help'); }}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  color: activeTab === 'help' ? '#405189' : '#878a99',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <i className="ri-question-line" style={{ fontSize: '20px' }}></i>
                <span style={{ fontSize: '11px' }}>Help</span>
              </button>
            </div>
          )}

          {/* Powered by CodeRom */}
          <div
            style={{
              textAlign: 'center',
              padding: '5px 0',
              fontSize: '11px',
              color: '#9ca3af',
              borderTop: activeTab === 'chat' ? '1px solid #e9ebec' : 'none',
              backgroundColor: 'white',
              flexShrink: 0
            }}
          >
            Powered by <span style={{ fontWeight: 600, color: '#6b7280' }}>CodeRom</span>
          </div>
        </div>
      )}

      {/* Image Lightbox Modal */}
      {lightboxImage && (
        <div
          onClick={() => setLightboxImage(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'zoom-out'
          }}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightboxImage(null); }}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              cursor: 'pointer',
              color: 'white',
              fontSize: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <i className="ri-close-line"></i>
          </button>
          <img
            src={lightboxImage}
            alt="Preview"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              objectFit: 'contain',
              borderRadius: '8px',
              cursor: 'default'
            }}
          />
          <a
            href={lightboxImage}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              bottom: '20px',
              right: '20px',
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 16px',
              cursor: 'pointer',
              color: 'white',
              fontSize: '14px',
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <i className="ri-external-link-line"></i>
            Open in new tab
          </a>
        </div>
      )}

      {/* Hover effect style */}
      <style>{`
        .floating-chat-btn:hover {
          transform: scale(1.1);
        }

        /* Typing cursor animation */
        .typing-cursor {
          animation: blink 1s infinite;
          font-weight: normal;
          color: #405189;
        }

        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }

        /* Typing dots animation */
        .typing-dot {
          display: inline-block;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: #405189;
          animation: typingDot 1.4s infinite ease-in-out both;
        }

        .typing-dot:nth-child(1) {
          animation-delay: -0.32s;
        }

        .typing-dot:nth-child(2) {
          animation-delay: -0.16s;
        }

        .typing-dot:nth-child(3) {
          animation-delay: 0s;
        }

        @keyframes typingDot {
          0%, 80%, 100% {
            transform: scale(0.6);
            opacity: 0.5;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }

        /* Page transition animations */
        @keyframes fcSlideIn {
          from { opacity: 0; transform: translateX(15px); }
          to { opacity: 1; transform: translateX(0); }
        }

        @keyframes fcSlideBack {
          from { opacity: 0; transform: translateX(-15px); }
          to { opacity: 1; transform: translateX(0); }
        }

        @keyframes fcFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </>
  );
};

export default FloatingChat;
